/**
 * Utilities for correlating events through the sync pipeline.
 * This helps track the relationship between original events and resulting state updates.
 */

export type CorrelationContext = {
  correlationId: string;
  originalEventType: string;
  originalVideoId?: string;
  timestamp: number;
  chain: string[];
};

/**
 * Generate a unique correlation ID.
 */
export const generateCorrelationId = (): string => {
  return `corr_${performance.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Create a correlation context for tracking an event through the pipeline.
 */
export const createCorrelationContext = (
  eventType: string,
  videoId?: string
): CorrelationContext => ({
  correlationId: generateCorrelationId(),
  originalEventType: eventType,
  originalVideoId: videoId,
  timestamp: performance.now(),
  chain: [eventType],
});

/**
 * Add a step to the correlation chain.
 */
export const addCorrelationStep = (
  context: CorrelationContext,
  step: string
): CorrelationContext => ({
  ...context,
  chain: [...context.chain, step],
});

/**
 * Create a correlation ID for a state update triggered by an event.
 */
export const createStateUpdateCorrelation = (
  originalCorrelationId: string,
  updateType: string
): string => {
  return `${originalCorrelationId}_${updateType}`;
};

/**
 * Create a correlation ID for video synchronization triggered by a state update.
 */
export const createSyncCorrelation = (
  stateCorrelationId: string,
  videoId: string
): string => {
  return `${stateCorrelationId}_sync_${videoId}`;
};

/**
 * Format a correlation chain for logging.
 */
export const formatCorrelationChain = (context: CorrelationContext): string => {
  return `[${context.correlationId}] ${context.chain.join(' → ')}`;
};

/**
 * Extract correlation info from a correlation ID.
 */
export const parseCorrelationId = (
  correlationId: string
): {
  originalId: string;
  steps: string[];
} => {
  const parts = correlationId.split('_');
  const originalId = parts[0];
  const steps = parts.slice(1);
  return {originalId, steps};
};
